/**
 * Trunnion AI - Policy Editor
 * ABAC Policy CRUD interface.
 */

import { useState } from 'react';
import { Plus, Shield, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { ABACPolicy } from '@/types';

// Placeholder using local state; in production wired to usePolicyList, etc.
export function PolicyEditor() {
  const [policies] = useState<ABACPolicy[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const columns: Column<ABACPolicy>[] = [
    {
      key: 'name',
      header: 'Policy Name',
      sortable: true,
      render: (p) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-trunnion-gold-500" />
          <span className="text-white font-medium">{p.name}</span>
        </div>
      ),
    },
    {
      key: 'effect',
      header: 'Effect',
      render: (p) => (
        <Badge variant={p.effect === 'allow' ? 'success' : 'error'}>
          {p.effect}
        </Badge>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (p) => <span className="text-trunnion-slate-300">{p.priority}</span>,
    },
    {
      key: 'conditions',
      header: 'Conditions',
      render: (p) => (
        <span className="text-trunnion-slate-300">{p.conditions.length} rules</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => (
        <Badge variant={p.isActive ? 'success' : 'default'} dot>
          {p.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-white">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-red-400">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">ABAC Policies</h2>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Create Policy
        </Button>
      </div>

      <DataTable<ABACPolicy>
        columns={columns}
        data={policies}
        keyExtractor={(p) => p.id}
        emptyMessage="No policies configured"
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Policy"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Create</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Policy Name" placeholder="Enter policy name" />
          <Input label="Description" placeholder="Describe the policy" />
          <Select
            label="Effect"
            options={[
              { value: 'allow', label: 'Allow' },
              { value: 'deny', label: 'Deny' },
            ]}
          />
          <Input label="Priority" type="number" placeholder="1" />
        </div>
      </Modal>
    </div>
  );
}
